import type { RoleEnumTypeV2 } from '@/features/auth/types'

/**
 * Objectif du programme, déclaré par l'équipe Constel (PROGRAM_TYPE) : il décide de ce que le tableau de bord
 * mesure. MATCHING : mise en relation ; SESSIONS : parcours de sessions ; CURRICULUM : cursus noté ; ON_DEMAND : à la demande.
 */
export const PROGRAM_TYPES = ['MATCHING', 'SESSIONS', 'CURRICULUM', 'ON_DEMAND'] as const
export type ProgramType = (typeof PROGRAM_TYPES)[number]

type ProgramSettingsType = {
  /** Non déclaré : le tableau de bord reprend le type d'après le nom du programme */
  programType: ProgramType | undefined
  /**
   * Affiche un avertissement de contact aux utilisateurs (MODERATION_PII_ENABLED)
   */
  moderationPiiEnabled: boolean

  mentorMatchingCriteria: string | undefined
  menteeMatchingCriteria: string | undefined
  mentorReportTemplate: string | undefined
  /**
   * Permet aux mentors et mentorés de renseigner leurs critères de matching
   */
  matchingCriteriaEnabled: boolean
  /**
   * Autorise les mentorés à déclencher le matching automatique pour trouver un mentor
   */
  matchingAutoSuggestEnabled: boolean
  matchingAdditionalInstructions: string | undefined
  maxUserPerGroup: number | undefined
  maxGroupPerMentee: number | undefined
  maxGroupPerMentor: number | undefined
  shouldMentorReviewSessionDeliverableReport: boolean
  shouldMenteeReviewSessionReport: boolean
  shouldMentorReviewSessionDeliverableRate: boolean
  shouldMentorReviewSessionPresence: boolean
  shouldReviewSessionRating: boolean
  shouldReviewSessionReview: boolean
  shouldReviewSessionDeliverable: boolean
  adminDashboardType: string | undefined
  defaultInactivityDays: number | undefined
  i18nMenteeName: string | undefined
  i18nMentorName: string | undefined
  hasMentorReviewSessionDeliverableReport: boolean
  hasMentorReviewSessionDeliverableRate: boolean
  hasMentorReviewSessionPresence: boolean
  hasReviewSessionRating: boolean
  hasReviewSessionReview: boolean
  hasReviewSessionDeliverable: boolean
  hasMenteeReviewSessionReport: boolean
  menteeReportTemplate: string | undefined
  menteeCanBookSession: boolean
  logo: string | undefined
  logoSmall: string | undefined
  favicon: string | undefined
  colorPrimary: string | undefined
  colorSecondary: string | undefined
}

export type V1PeriodType = {
  id: string
  name: string
  startAt: string
  endAt: string
}

export type V2PeriodType = {
  id: string
  name: string
  /**
   * @deprecated This field is no longer used. Use session objectives instead.
   */
  sessionsGoal?: number
  startAt: string
  endAt: string
}

type V1ProgramSettingsKeysType =
  | 'MENTOR_REPORT_TEMPLATE'
  | 'MAX_USER_PER_GROUP'
  | 'MATCHING_MENTEE_CRITERIA'
  | 'MATCHING_MENTOR_CRITERIA'
  | 'MATCHING_CRITERIA_ENABLED'
  | 'MATCHING_AUTO_SUGGEST_ENABLED'
  | 'MENTOR_REVIEW_SESSION_DELIVERABLE_REPORT_STEP'
  | 'MENTOR_REVIEW_SESSION_DELIVERABLE_RATE_STEP'
  | 'MENTOR_REVIEW_SESSION_PRESENCE_STEP'
  | 'REVIEW_SESSION_RATING_STEP'
  | 'REVIEW_SESSION_REVIEW_STEP'
  | 'REVIEW_SESSION_DELIVERABLE_STEP'
  | 'ADMIN_DASHBOARD_TYPE'
  | 'PROGRAM_TYPE'
  | 'MAX_GROUP_PER_MENTEE'
  | 'MAX_GROUP_PER_MENTOR'
  | 'MATCHING_ADDITIONAL_INSTRUCTIONS'
  | 'DEFAULT_INACTIVITY_DAYS'
  | 'I18N_MENTEE_NAME'
  | 'I18N_MENTOR_NAME'
  | 'MENTEE_CAN_BOOK_SESSION'
  | 'MENTEE_REVIEW_SESSION_REPORT_STEP'
  | 'MENTEE_REPORT_TEMPLATE'
  | 'MODERATION_PII_ENABLED'
  | 'LOGO'
  | 'LOGO_SMALL'
  | 'FAVICON'
  | 'COLOR_PRIMARY'
  | 'COLOR_SECONDARY'
/**
 * MODERATION_LAW_ENABLED
 * MODERATION_FINANCIAL_ENABLED
 * MODERATION_HEALTH_ENABLED
 * MODERATION_SELF_HARM_ENABLED
 * MODERATION_SEVERE_CONTENT_ENABLED
 */

export type V1ProgramType = {
  id: string
  name: string
  description: string
  color?: string | null
  periods: V1PeriodType[]
  settings: { name: V1ProgramSettingsKeysType; value: string }[]
  users?: {
    id: string
    programId: string
    userId: string
    createdAt: string
    updatedAt: string
  }[]
}

export type V2ProgramType = {
  id: string
  name: string
  description: string | null
  color: string | null
  periods: V1PeriodType[]
  latestPeriod?: V2PeriodType
  settings: ProgramSettingsType
  users: {
    id: string
  }[]
}

export type DoesUserExistsInProgramDTOType = {
  userId: string
  programId: string
}

export type AddUserToProgramDTOType = {
  userId: string
  programId: string
  roles: RoleEnumTypeV2[]
}

export type UpdateUserEmailInProgramDTOType = {
  userId: string
  programId: string
  email: string
}

export type CreateProgramSettingsType = {
  globalSettings: {
    programName: string | undefined
    programDescription: string | null
    selectedPeriodId: string
    programPeriodStartAt: string
    programPeriodEndAt: string
  }
  settings: ProgramSettingsType
}

export type UpdateProgramSettingsType = {
  globalSettings: {
    programId: string
    programName: string | undefined
    programDescription: string | null
    selectedPeriodId: string
    programPeriodStartAt: string
    programPeriodEndAt: string
  }
  settings: ProgramSettingsType
}

export type DoesUserExistsInProgramResponseType = {
  exist: boolean
}
